/**
 * Copyright (c) Stanislav Shishankin
 *
 * This source code is licensed under the MIT license.
 */

const {LinkToViewElement, LinkToView, LinkToElement} = require('./Link');
const {LizziViewComponent, ArrayInsertBeforeLink, ViewInsertBeforeLink} = require('./Lizzi');
const {zzReactive, zzStringConcat, zzArray, zzObject} = require('../index');
const {Event} = require('../Event');
const {Router} = require('../Router');
const { ViewComponent } = require('./View');

class InputLink extends LinkToViewElement{
    addEventsToEL(el, view){
        view.ev(this.value, 'change', (event) => {
            if (el.value !== event.value){
                el.value = event.value;
            }
        }, this);
        
        view.ev(el, 'input', () => {
            if (el.value !== this.value.value){
                let value = this.inputChangeFn(el.value);
                if (value !== undefined){
                    this.value.value = value;
                }
            }
        }, false);
        
        view.ev(el, 'blur', () => {
            if (el.value !== this.value.value){
                el.value = this.value.value;
            }
        }, false).run();
    }

    constructor(selector, value, inputChangeFn){
        super(selector);
        
        this.value = value;
        this.inputChangeFn = inputChangeFn?inputChangeFn:(v) => v;
    }
}

class AutoResizeTextarea extends LinkToViewElement{
    delayedResize (text) {
        text.style.height = 'auto';
        text.style.height = text.scrollHeight+'px';
    }
    
    addEventsToEL(text, view){
        var fn = Event.Defer(this.delayedResize.bind(this, text));
        view.ev(text, 'focus', fn, false);
        view.ev(text, 'change', fn, false);
        view.ev(text, 'input', fn, false);
        view.ev(text, 'cut', fn, false);
        view.ev(text, 'paste', fn, false);
        view.ev(text, 'drop', fn, false);
        view.ev(text, 'keydown', fn, false);

        if (this.value instanceof zzReactive){
            view.ev(this.value, 'change', fn);
        }

        fn();
    }

    constructor(selector, value){
        super(selector);
        
        this.value = value || null;
    }
};

class TextLink extends LinkToViewElement{
    addEventsToEL(el){
        let DOMText = document.createTextNode('');
        this.value.change( () => {
            DOMText.data = this.value.value;
        }, this).run();

        el.appendChild(DOMText);

        this.nodes.push(DOMText);
    }

    clearEvents(){
        this.value.off( this );
        
        for (let node of this.nodes){
            node.parentNode.removeChild(node);
        }
        this.nodes = [];
    }

    constructor(selector, value){
        super(selector);
        
        this.value = new zzStringConcat(value);
        this.nodes = [];
    }
};

class TextInsertBeforeLink extends LinkToView{
    addEvents(){
        let DOMText = document.createTextNode('');
        this.value.change( () => {
            DOMText.data = this.value.value;
        }, this).run();

        this.before.parentNode.insertBefore(DOMText, this.before);
        this.nodes.push(DOMText);
    }

    clearEvents(){
        this.value.off( this );
        
        for (let node of this.nodes){
            node.parentNode.removeChild(node);
        }
        this.nodes = [];
    }

    constructor(before, value){
        super();
        
        this.value = new zzStringConcat(value);
        this.before = before;
        this.nodes = [];
    }
};

class HtmlLink extends LinkToViewElement{
    addEventsToEL(el){
        this.model.change( () => {
            el.innerHTML = this.value.value;
        }, this).run();
    }

    clearEvents(){
        this.value.off( this );
    }

    constructor(selector, value){
        super(selector);
        
        this.value = new zzStringConcat(value);
    }
}

class SwitchClassLink extends LinkToViewElement{
    removeClass(el, className){
        let cels = el.className.split(' ');
        let i = cels.indexOf(className);
        if (i !== -1){
            cels.splice(i, 1);
            el.className = cels.join(' ');
        }
    }
    
    addClass(el, className){
        let cels = el.className.split(' ');
        cels.push(className);
        el.className = cels.join(' ');
    }
    
    addEventsToEL(el, view){
        this.skeys.forEach(v => this.removeClass(el, this.sets[v]));
        
        view.ev(this.value, 'change', (ev) => {
            let s = this.sets.find(f => f.value === ev.last);
            if (s){
                this.removeClass(el, s.class);
            }

            s = this.sets.find(f => f.value === ev.value);
            if (s){
                this.addClass(el, s.class);
            }
        }, this).run({last: null, value: this.value.value});

        view.ev(el, 'click', () => {
            let k = this.skeys.indexOf( this.value.value )+1;

            if (!this.skeys[k]){k = 0;}

            this.value.value = this.skeys[k];
        }, false);
    }

    constructor(selector, value, sets){
        super(selector);
        
        this.value = value;
        this.sets = sets?sets:[
            {value: false, class: 'off'},
            {value: true, class: 'on'}
        ];
        this.skeys = this.sets.map(s => s.value);
    }
}

class CheckboxLink extends LinkToViewElement{
    addEventsToEL(el, view){
        view.ev(this.value, 'change', (ev) => {
            if (el.value !== 'on'){
                el.checked = (ev.value === el.value);
            }else{
                el.checked = ev.value?true:false;
            }
        }, this).run({last: null, value: this.value.value});

        view.ev(el, 'click', (e) => {
            if (el.checked){
                this.value.value = el.value?el.value:true;
            }else{
                this.value.value = el.value?'':false;
            }
        }, false);
    }

    constructor(selector, value){
        super(selector);
        
        this.value = value;
    }
}

class SelectLink extends LinkToViewElement{
    removeClass(el, className){
        let cels = el.className.split(' ');
        let i = cels.indexOf(className);
        if (i !== -1){
            cels.splice(i, 1);
            el.className = cels.join(' ');
        }
    }
    
    addClass(el, className){
        let cels = el.className.split(' ');
        cels.push(className);
        el.className = cels.join(' ');
    }
    
    addEventsToEL(el, view){        
        view.ev(this.value, 'change', (ev) => {
            let value = el.getAttribute(this.attrName) || (el.dataset[this.attrName]);
            if (value){
                if (ev.value === value){
                    this.addClass(el, this.className);
                }else{
                    this.removeClass(el, this.className);
                }
            }else if (!ev.value){
                this.addClass(el, this.className);
            }else{
                this.removeClass(el, this.className);
            }
        }, this).run({last: null, value: this.value.value});

        view.ev(el, 'click', (e) => {
            let value = el.getAttribute(this.attrName) || (el.dataset[this.attrName]);
            if (value){
                this.value.value = value;
            }else{
                this.value.value = false;
            }
        }, false);
    }

    constructor(selector, value, className, attrName){
        super(selector);
        
        this.value = value;
        this.className = className;
        this.attrName = attrName || 'value';
    }
}

class ElementsLink extends LinkToViewElement{
    addEvents(view){
        if (this.initFn){
            if (this.initFn instanceof LinkToElement){
                this.initFn.addEvents(this.elements, view);
            }else{
                for (let els of this.elements){
                    if (!(els instanceof HTMLElement)){
                        continue;
                    }
            
                    this.initFn(els, view);
                }
            }
        }
    }
    
    clearEvents(view){
        if (this.initFn instanceof LinkToElement){
            this.initFn.clearEvents(this.elements, view);
        }
        
        if (this.destroyFn){
            for (let els of this.elements){
                if (!(els instanceof HTMLElement)){
                    continue;
                }
        
                this.destroyFn(els, view);
            }
        }
    }

    linkElements(view, selector){
        if (this.initFn instanceof LinkToElement){
            this.initFn.linkElements(view, selector);
        }
    }
    
    constructor(selector, initFn, destroyFn){
        super(selector);
        
        this.initFn = initFn;
        this.destroyFn = destroyFn;
    }
}

class AttributeLink extends LinkToViewElement{
    onModelChange(attr, model, el){
        let value = model.value;
        
        if (!value && value !== ''){
            el.removeAttribute(attr);
        }else if(value === true){
            el.setAttribute(attr, '');
        }else{
            el.setAttribute(attr, value);
        }
    }
    
    setupAttr(attr, model, el){
        model.change(this.onModelChange.bind(this, attr, model, el), this);

        this.onModelChange(attr, model, el);
    }
    
    addEventsToEL(el){
        if (!(el instanceof Element)){
            return;
        }

        for (let name in this.attr){
            this.setupAttr(name, this.attr[name], el);
        }
    }

    clearEvents(){
        for (let name in this.attr){
            this.attr[name].off( this );
        }
    }

    constructor(selector, attr){
        super(selector);

        for (let name in attr){
            if (!(attr[name] instanceof zzReactive)){
                attr[name] = new zzStringConcat(attr[name]);
            }
        }
        
        this.attr = attr;
    }
}

class StyleLink extends AttributeLink{
    onModelChange(el, style, model){
        el.style[style] = model.value;
    }
    
    setupAttr(style, model, el){
        model.change(this.onModelChange.bind(this, el, style, model), this);

        this.onModelChange(el, style, model);
    }
}

class ClassTextLink extends LinkToViewElement{
    addEventsToEL(el){
        if (!(el instanceof HTMLElement)){
            return;
        }

        let listener = function(event){
            var cels = el.className.split(' ');
            
            if (event.last){
                var remove = event.last.split(' ');
                for (var r in remove){
                    let i = cels.indexOf(remove[r]);
                    if (i !== -1){
                        cels.splice(i, 1);
                    }            
                }
            }

            cels.push(event.value);

            el.className = cels.filter(cls => cls).join(' ').replace(/\s+/gmi,' ');
        };

        if (this.value instanceof zzReactive){
            this.value.change( listener, this);
            
            listener({
                last: null,
                value: this.value.value
            });
        }else{
            el.className += ' '+this.value;
        }
    }

    clearEvents(){
        for (let el of this.elements){
            var cels = el.className.split(' ');
            let value;

            if (this.value instanceof zzReactive){
                this.value.off( this );
                value = this.value.value;
            }else{
                value = this.value;
            }

            var remove = value.split(' ');
            for (var r in remove){
                let i = cels.indexOf(remove[r]);
                if (i !== -1){
                    cels.splice(i, 1);
                }            
            }

            el.className = cels.filter(cls => cls).join(' ').replace(/\s+/gmi,' ');
        }
    }

    constructor(selector, value){
        super(selector);
        
        this.value = new zzStringConcat(value);
    }
}

class ClassObjectLink extends LinkToViewElement{
    setupClass(className, model, el){
        if (model instanceof zzReactive){
            let listener = () => {
                var cels = el.className.split(' ');

                var remove = className.split(' ');
                for (var r in remove){
                    let i = cels.indexOf(remove[r]);
                    if (i !== -1){
                        cels.splice(i, 1);
                    }            
                }

                if (model.value){
                    cels.push(className);
                }

                el.className = cels.join(' ').replace(/\s+/gmi,' ');
            };

            model.change( listener, this);

            listener({
                last: null,
                value: model.value
            });
        }else if (model){
            el.className += ' '+className;
        }
    }
    
    addEventsToEL(el){
        if (!(el instanceof HTMLElement)){
            return;
        }

        for (let name in this.classes){
            this.setupClass(name, this.classes[name], el);
        }
    }

    clearEvents(){
        for (let name in this.classes){
            if (this.classes[name] instanceof zzReactive){
                this.classes[name].off( this );
            }
        }
    }

    constructor(selector, classes){
        super(selector);
        
        this.classes = classes;
    }
}

class ClassLink extends LinkToViewElement{
    addEvents(){}

    linkElements(view, selector){
        let value = this.value;
        if (typeof value === 'string' || value instanceof zzReactive || Array.isArray(value)){
            view.link( new ClassTextLink(selector, value) );
        }else{
            view.link( new ClassObjectLink(selector, value) );
        }
    }

    constructor(selector, value){
        super(selector);
        
        this.value = value;
    }
}

class ClickLink extends LinkToViewElement{
    addEventsToEL(el, view){
        view.ev(el, 'click', this.fn.bind(this.self?this.self:view, el, view));
    }

    constructor(selector, fn, self){
        super(selector);
        
        this.fn = fn;
        this.self = self;
    }
}

class IfLink extends LinkToViewElement{
    addEventsToEL(el){
        let DOMEmpty = document.createTextNode('');
        el.parentNode.insertBefore(DOMEmpty, el);

        let last = 1;

        this.value.change( () => {
            let visible = Boolean(this.value.value) ^ this.inverse;
            if (last !== visible){
                if (visible){
                    DOMEmpty.parentNode.insertBefore(el, DOMEmpty);
                }else{
                    el.remove();
                }

                last = visible;
            }
        }, this).run();
        
        this.nodes.push({
            text: DOMEmpty,
            el: el
        });
    }

    clearEvents(){
        this.value.off( this );
        
        for (let node of this.nodes){
            node.text.parentNode.insertBefore(node.el, node.text);
            node.text.parentNode.removeChild(node.text);
        }
        this.nodes = [];
    }

    constructor(selector, value, inverse){
        super(selector);

        if (!(value instanceof zzReactive)){
            console.error('Error: condition is not zzReactive');
        }
        
        this.value = value;
        this.nodes = [];
        this.inverse = inverse || false;
    }
}

class RouteHrefLink extends LinkToViewElement{
    addEventsToEL(el, view){
        view.ev(el, 'click', function(ev){
            ev.preventDefault();
            Router.go( el.getAttribute( 'href' ) );
        }.bind(this));
    }
}

class RouteLink extends LinkToViewElement{
    addEvents(){}

    linkElements(view, selector){
        view.link( new AttributeLink(selector, {
                'href': this.value
            }))
            .link( new RouteHrefLink(selector) );
    }

    constructor(selector, value){
        super(selector);
        
        this.value = value;
    }
}

class LinkParser{
    findInObject(text){
        let inside = text.split('.');
        let value = this.object;

        while(inside.length > 0){
            let index = inside.shift();

            if (!(index in value)){
                console.error('Template Parse Error: index `', index, '` in variable {{', text, '}} is not defined');
    
                return undefined;
            }

            value = value[index];
        }
        return value;
    }

    parseText(el){
        let match;

        let index = 0;
        let text = el.data;
        let find = /\{\{\s*([\w.]+)\s*(?:\|\s*([\w]+)\s*)?\}\}/g;

        while ((match = find.exec(text)) != null) {
            if (index !== match.index){
                el.parentNode.insertBefore(document.createTextNode(text.substring(index, match.index)), el);
            }

            let EmptyNode = document.createTextNode('');
            el.parentNode.insertBefore(EmptyNode, el);

            index = find.lastIndex;

            let value = this.findInObject(match[1]);
            if (value === undefined) continue;

            if (value instanceof zzArray){
                this.view.link( new ArrayInsertBeforeLink(EmptyNode, value, match[2]) );
            }else if (value instanceof zzObject || value instanceof ViewComponent || (match[2] && value[match[2]])){
                this.view.link( new ViewInsertBeforeLink(EmptyNode, value, match[2]) );
            }else{
                this.view.link( new TextInsertBeforeLink(EmptyNode, value) );
            }
        }

        el.parentNode.insertBefore(document.createTextNode(text.substring(index, text.length)), el);
        el.parentNode.removeChild(el);
    }

    __camelize(str) {
        return str.replace(/(?:-(\w))/g, (match) => match.substring(1).toUpperCase());
    }

    parseAttrs(el){
        let attrs = el.attributes;

        let attrsObj = {};
        for(var i = attrs.length - 1; i >= 0; i--) {
            let attr = attrs[i];

            switch(attr.name){
                //case 'style':
                case 'zz-style':{
                    let styleMatch;
                    let styleText = attr.value;
                    let styleFind = /\s*([\w.-]+)\s*:([^;]+)/g;

                    let styleObj = {};

                    while ((styleMatch = styleFind.exec(styleText)) != null) {
                        let match;
                        let index = 0;
                        let text = styleMatch[2];
                        let find = /\{\{\s*([\w.-]+)\s*\}\}/g;
                        let style = [];

                        while ((match = find.exec(text)) != null) {
                            style.push(text.substring(index, match.index));
    
                            let value = this.findInObject(match[1]);
                            if (value === undefined) continue;
    
                            style.push(value);
    
                            index = find.lastIndex;
                        }
    
                        style.push(text.substring(index, text.length));

                        styleObj[this.__camelize(styleMatch[1])] = style;
                    }

                    if (Object.keys(styleObj).length > 0){
                        this.view.link( new StyleLink(el, styleObj) );
                    }

                    el.removeAttribute(attr.name);
                }break;
                case 'class':{
                    let match;

                    let index = 0;
                    let text = attr.value;
                    let find = /\{\{\s*([\w.-]+)\s*(?::\s*([\w.]+)\s*)?\}\}/g;

                    let className = '';

                    while ((match = find.exec(text)) != null) {
                        className += text.substring(index, match.index);

                        if (match[2] === undefined){
                            let value = this.findInObject(match[1]);
                            if (value === undefined) continue;

                            this.view.link( new ClassLink(el, value) );
                        }else{
                            let value = this.findInObject(match[2]);
                            if (value === undefined) continue;
        
                            let ob = {};
                            ob[match[1]] = value;
                            this.view.link( new ClassLink(el, ob) );
                        }

                        index = find.lastIndex;
                    }

                    className += text.substring(index, text.length);
                    attr.value = className;
                }break;
                case 'zz-if':
                    let match;
                    let text = attr.value;
                    let find = /\{\{(!)?\s*([\w.]+)\s*\}\}/g;

                    if ((match = find.exec(text)) != null) {
                        let value = this.findInObject(match[2]);
                        if (value === undefined) continue;
    
                        this.view.link( new IfLink(el, value, !!match[1]) );
                    }else{
                        console.error(`Template Parse Error: wrong defined zz-if="${ text }". Forget {{...}}?`);
                    }

                    el.removeAttribute(attr.name);

                    break;
                default:{
                    let match;
                    let array = [];

                    let index = 0;
                    let text = attr.value;
                    let find = /\{\{\s*([\w.-]+)\s*(?::\s*([\w.]+)\s*)?\}\}/g;

                    while ((match = find.exec(text)) != null) {
                        if (index !== match.index){
                            array.push(text.substring(index, match.index));
                        }

                        index = find.lastIndex;

                        let value = this.findInObject(match[1]);
                        if (value === undefined) continue;
    
                        array.push( value );
                    }

                    if (index !== text.length){
                        array.push(text.substring(index, text.length));
                    }

                    attrsObj[attr.name] = array;
                }
            }
        }

        if (Object.keys(attrsObj).length > 0){
            this.view.link( new AttributeLink(el, attrsObj) );
        }
    }

    findAndParseDOM(childs){
        childs = Array.prototype.slice.call(childs);

        for (let el of childs){
            if (el.nodeType === Node.TEXT_NODE){
                this.parseText(el);
            }

            if (el.nodeType === Node.ELEMENT_NODE){
                this.parseAttrs(el);

                this.findAndParseDOM(el.childNodes);
            }
        }
    }

    constructor(childs, view, object){
        this.object = object;
        this.view = view;

        this.findAndParseDOM(childs);
    }
}

class HtmlViewComponent extends LizziViewComponent{
    input(selector, value, changeFn){
        return this.link( new InputLink(selector, value, changeFn) );
    }
    
    inputInteger(selector, value){
        return this.input(selector, value, (val) => {
             val = parseInt(val);
             return isNaN(val)?undefined:val;
        });
    }

    inputFloat(selector, value){
        return this.input(selector, value, (val) => {
             val = parseFloat(val);
             return isNaN(val)?undefined:val;
        });
    }

    checkbox(selector, value){
        return this.link( new CheckboxLink(selector, value) );
    }
    
    radio(selector, value, sets){
        return this.link( new CheckboxLink(selector, value, sets) );
    }

    select(selector, value, className, attrName){
        return this.link( new SelectLink(selector, value, className, attrName) );
    }

    elements(selector, initFn, destroyFn){
        return this.link( new ElementsLink(selector, initFn, destroyFn) );
    }
     
    autoResizeTextarea(selector, value){
        return this.link( new AutoResizeTextarea(selector, value) );
    }

    text(selector, value, append){
        return this.link( new TextLink(selector, value, append) );
    }
    
    html(selector, value){
        return this.link( new HtmlLink(selector, value) );
    }

    switch(selector, value, sets){
        return this.link( new SwitchClassLink(selector, value, sets) );
    }

    attr(selector, value){
         return this.link( new AttributeLink(selector, value) );
    }

    style(selector, value){
         return this.link( new StyleLink(selector, value) );
    }

    class(selector, value){
        return this.link( new ClassLink(selector, value) );
    }

    click(selector, fn, self){
        return this.link( new ClickLink(selector, fn, self) );
    }

    if(selector, value, inverse){
        return this.link( new IfLink(selector, value, inverse) );
    }

    route(selector, data){
        if (data === undefined){
            return this.link( new RouteLink(selector) );
        }else{
            return this.link( new RouteLink(selector, data) );
        }
    }

    parseTemplate(object){
        new LinkParser(this.__zzDOMChilds, this, object || this);

        return this;
    }
}

class Template{
    find(selector){
        let find = this.DOM.querySelector(selector);

        if (!find){
            console.error('Template error: can\'t find ', selector);
        }
        
        return find;
    }

    createView(selector){
        return new HtmlViewComponent( this.find(selector) );
    }

    constructor(body){
        this.DOM = body;
    }
}

function Loader(html){
    var tmp = document.implementation.createHTMLDocument();
    tmp.body.innerHTML = html;

    return new Template(tmp.body);
}

module.exports = {
    InputLink, AutoResizeTextarea, TextLink, HtmlLink, 
    SwitchClassLink, CheckboxLink, SelectLink, ElementsLink, 
    AttributeLink, StyleLink, ClassTextLink, ClassObjectLink, 
    ClassLink, ClickLink, IfLink, RouteHrefLink, RouteLink,
    Loader, HtmlViewComponent
}